import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    clearNewAppointment,
    createSchedulingAssistantAppointment,
    createSchedulingAssistantAppointmentSlot,
    createUserCalendarAppointment,
    createUserCalendarAppointmentSlot,
    createUserEvent,
    setCurrentUserEvent,
    setOpenAppointmentSchedulerPanel,
    setUsersCurrentAppointment
} from '../../../store/calendarService';
import {
    loadLocationsByServiceId,
    loadServiceDetailsByService,
    loadUserEventTypes
} from '../../../store/directusService';
import {
    clearParticipantsSearch,
    searchParticipants,
    setCurrentlySelectedParticipant
} from '../../../store/participantService';
import { loadUser } from '../../../store/userService';
import { todayDate } from '../../../utils/dateFunctions';
import { getConfiguredItems, getObjFromId } from '../../../utils/directusFunctions';
import { MAX_URL } from '../../../utils/formValidation/commonConstants';
import { hasRole } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import DateSelect from '../../formElements/DateSelect';
import RadioButtons from '../../formElements/RadioButtons';
import SingleSelect from '../../formElements/SingleSelect';
import StaticField from '../../formElements/StaticField';
import TextAreaField from '../../formElements/TextAreaField';
import TextInputField from '../../formElements/TextInputField';
import Panel from '../../ui/panel/Panel';
import AppointmentTypeSelect from '../components/AppointmentTypeSelect';
import LocationSelect from '../components/LocationSelect';
import MandatoryActivitiesSwitch from '../components/MandatoryActivitiesSwitch';
import StartAndEndTimesSelect from '../components/StartAndEndTimesSelect';
import StartAppointmentRow from '../components/StartAppointmentRow';
import { acceptedRoles, getServiceIds } from '../utils/calendarUtils';
import { validateAppointment } from '../validation/validateUserAppointmentCreate';
import { validateSlots } from '../validation/validateUserAppointmentSlotCreate';
import { validateUserEvent } from '../validation/validateUserEventCreate';
import { ADDITIONAL_DETAILS_LIMIT } from '../validation/validationFunctions';

import app from '../../../app.module.css';
import sidePanel from '../styles/schedulePanel.module.css';

const APPOINTMENT = 'appointment';
const APPOINTMENT_SLOT = 'appointmentSlot';
const USER_EVENT = 'userEvent';
const _NOTES_LIMIT = 250;

const UserAppointmentCreate = ({ newAppointment, currentCalendarOwner, calendar }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        typeOfEvent: APPOINTMENT,
        typeId: '',
        userEventTypeId: '',
        userEventType: '',
        userId: '',
        locationId: '',
        startTime: '08:00',
        endTime: '09:00',
        date: todayDate(),
        serviceId: '',
        teamId: '',
        participantId: '',
        participantIds: [],
        mandatory: false,
        numberOfSlots: null,
        type: '',
        location: {},
        service: '',
        participant: '',
        remoteCallUrl: '',
        additionalDetails: '',
        repeat: 'NO_REPEAT',
        repeatUntilDate: '',
        notes: ''
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [isEditable, setIsEditable] = useState(false);
    const [filteredServices, setFilteredServices] = useState([]);
    const [filteredTeams, setFilteredTeams] = useState([]);
    const [filteredAppointmentTypes, setFilteredAppointmentTypes] = useState([]);
    const [filteredUserEventTypes, setFilteredUserEventTypes] = useState([]);
    const [filteredParticipants, setFilteredParticipants] = useState([]);
    const [serviceIds, setServiceIds] = useState([]);
    const [selectedParticipants] = useState([]);
    const [isDisabledStartAppointmentButton, setIsDisabledStartAppointmentButton] = useState(false);
    const [currentParticipant, setCurrentParticipant] = useState({});
    const [isDisabledSaveButton, setIsDisabledSaveButton] = useState(false);
    const [isCreated, setIsCreated] = useState(false);
    const [showUntilDate, setShowUntilDate] = useState(false);
    const [typeOfEventRadioButtons, setTypesOfEventRadioButtons] = useState([
        { id: 'appointment', label: 'Appointment', onChangeVal: 'appointment' },
        {
            id: 'appointmentSlot',
            label: 'Appointment Slot',
            onChangeVal: 'appointmentSlot'
        }
    ]);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const participantsSearch = useSelector(
        (state) => state.entities.participantService.participantsSearch
    );
    const participantsSearchMetaData = useSelector(
        (state) => state.entities.participantService.participantsSearchMetaData
    );
    const loadingParticipantsSearch = useSelector(
        (state) => state.entities.participantService.loadingParticipantsSearch
    );
    const serviceDetailsById = useSelector(
        (state) => state.entities.directusService.serviceDetailsById
    );
    const users = useSelector((state) => state.entities.userService.usersByLoggedInUserServiceIds);
    const user = useSelector((state) => state.entities.userService.currentUser);
    const locationsByServiceId = useSelector(
        (state) => state.entities.directusService.locationsByServiceId
    );
    const appointmentTypeDetails = useSelector(
        (state) => state.entities.directusService.appointmentTypeDetails
    );
    const userEventTypes = useSelector((state) => state.entities.directusService.userEventTypes);
    const openAppointmentSchedulerPanel = useSelector(
        (state) => state.entities.calendarService.openAppointmentSchedulerPanel
    );
    const newCreatedAppointment = useSelector(
        (state) => state.entities.calendarService.newAppointment
    );
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        calendar !== 'schedulingAssistant' &&
            setTypesOfEventRadioButtons((prev) => [
                ...prev,
                { id: 'userEvent', label: 'User Event', onChangeVal: 'userEvent' }
            ]);
        if (!currentCalendarOwner || Object.keys(currentCalendarOwner).length < 1) return;

        const serviceIds = getServiceIds(currentCalendarOwner);
        if (!serviceIds?.length) return;
        setServiceIds(serviceIds);
        const missingServiceDetailIds = serviceIds.filter((el) => !(el in serviceDetailsById));
        if (missingServiceDetailIds.length)
            dispatch(loadServiceDetailsByService(missingServiceDetailIds));
        setIsEditable(hasRole(acceptedRoles, roles));
        return () => dispatch(clearParticipantsSearch());
    }, []);

    useEffect(() => {
        if (participantsSearch?.length < 1) {
            setFilteredParticipants([]);
            return;
        }
        if (
            newEntry.serviceId &&
            !participantsSearchMetaData.last &&
            !loadingParticipantsSearch &&
            'number' in participantsSearchMetaData
        ) {
            dispatch(
                searchParticipants(
                    { serviceId: newEntry.serviceId },
                    participantsSearchMetaData.number + 1
                )
            );
        } else if (participantsSearchMetaData?.last) {
            configParticipants();
        }
    }, [participantsSearch, participantsSearchMetaData, loadingParticipantsSearch]);

    useEffect(() => {
        if (
            !currentCalendarOwner?.id ||
            !(currentCalendarOwner?.primaryService in serviceDetailsById)
        )
            return;
        setNewEntry({
            ...newEntry,
            serviceId: currentCalendarOwner.primaryService,
            userId: currentCalendarOwner.id,
            startTime: newAppointment.startTime,
            endTime: newAppointment.endTime,
            date: newAppointment.date,
            service: serviceDetailsById[currentCalendarOwner?.primaryService]?.name || ''
        });
    }, [serviceDetailsById]);

    useEffect(() => {
        if (serviceIds.length < 1 || Object.values(serviceDetailsById).length < 1) return;
        setFilteredServices(
            serviceIds
                .map((el) => (el in serviceDetailsById ? serviceDetailsById[el] : null))
                .filter((el) => el)
        );
    }, [serviceDetailsById, serviceIds]);

    useEffect(() => {
        if (!newEntry.serviceId) return;
        const contractId = serviceDetailsById[newEntry.serviceId]?.contract?.id;
        if (contractId && appointmentTypeDetails?.length) {
            setFilteredAppointmentTypes(getConfiguredItems(appointmentTypeDetails, contractId));
        }
        if (!(newEntry.serviceId in locationsByServiceId)) {
            dispatch(loadLocationsByServiceId([newEntry.serviceId]));
        }
        if (newEntry.typeOfEvent === APPOINTMENT_SLOT) updateTeams();
        dispatch(searchParticipants({ serviceId: newEntry.serviceId }, 0));
        if (newEntry.participant) {
            setNewEntry((prev) => ({
                ...prev,
                participantId: '',
                participant: '',
                mandatory: false
            }));
            setCurrentParticipant({});
            dispatch(setCurrentlySelectedParticipant({}));
        }
    }, [newEntry.serviceId]);

    useEffect(() => {
        if (!newEntry.serviceId) return;
        configParticipants();
    }, [newEntry.date]);

    useEffect(() => {
        if (newEntry.typeOfEvent === APPOINTMENT_SLOT && newEntry.serviceId) updateTeams();
    }, [user]);

    useEffect(() => {
        if (!loggedInUser?.contractIds?.length || userEventTypes?.length < 1) return;
        const filtered = userEventTypes.filter((el) =>
            el.contract?.find((entry) => loggedInUser?.contractIds?.includes(entry.contract_id.id))
        );
        if (filtered) setFilteredUserEventTypes(filtered);
    }, [userEventTypes]);

    useEffect(() => {
        setIsDisabledSaveButton(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (
            successMessage === `Appointment ${newEntry.type} has been created` ||
            successMessage === `Appointment slot ${newEntry.type} has been created` ||
            successMessage === `User event ${newEntry.userEventType} has been created`
        ) {
            setIsCreated(true); // to prevent further edits by User. Stays open only for 'Starting' the appointment
            if (newEntry.participantId && newEntry.typeOfEvent !== APPOINTMENT_SLOT) {
                setIsDisabledStartAppointmentButton(false);
            } else onPanelClose();
        }
    }, [successMessage]);

    useEffect(() => {
        if (errorMessage) {
            setIsDisabledSaveButton(false);
        }
    }, [errorMessage]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false } }));
    };

    const updateTeams = () => {
        let adviserTeams = [];
        if (!('primaryTeam' in currentCalendarOwner)) {
            const newUsers = 'id' in user ? [user, ...users] : [...users];
            const adviser = newUsers.find((el) => el.id === currentCalendarOwner.id);
            if (adviser) adviserTeams = [adviser.primaryTeam, ...adviser.otherTeams];
            else dispatch(loadUser(currentCalendarOwner.id));
        } else
            adviserTeams = [currentCalendarOwner.primaryTeam, ...currentCalendarOwner.otherTeams];
        setFilteredTeams(adviserTeams.filter((el) => el.serviceId === newEntry.serviceId));
    };

    const configParticipants = () => {
        setFilteredParticipants(
            participantsSearch
                .filter(
                    (el) =>
                        el.serviceId === newEntry.serviceId &&
                        new Date(newEntry.date) >= new Date(el.referralDate) &&
                        el.status.toLowerCase() !== 'closed'
                )
                .map((el) => ({ ...el, name: `${el.firstName} ${el.lastName} - ${el.ptCode}` }))
        );
    };

    // EVENT HANDLERS
    const onPanelClose = () => {
        setNewEntry(initialState);
        dispatch(setUsersCurrentAppointment({}));
        dispatch(setCurrentUserEvent({}));
        dispatch(setOpenAppointmentSchedulerPanel(false));
        dispatch(clearNewAppointment());
    };

    const onServiceChange = (id) => {
        clearError('serviceId');
        if (id === newEntry.serviceId) return;
        setIsDisabledStartAppointmentButton(true);
        setNewEntry((prev) => ({
            ...prev,
            serviceId: id,
            service: serviceDetailsById[id]?.name || '',
            locationId: '',
            location: {},
            participantId: '',
            participant: '',
            teamId: '',
            typeId: '',
            type: ''
        }));
    };

    const onUpdate = (key, value) => {
        clearError(key);
        setIsDisabledStartAppointmentButton(true);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateTime = (key, value) => {
        clearError(key === 'endTime' ? 'startTime' : 'endTime');
        onUpdate(key, value);
    };

    const onUpdateDate = (date) => {
        clearError('repeatUntilDate');
        clearError('startTime');
        clearError('endTime');
        onUpdate('date', date);
    };

    const onUpdateAppointmentType = (key, value, type) => {
        onUpdate(key, value);
        setNewEntry((prev) => ({ ...prev, type }));
    };

    const onUpdateLocation = (key, value, location) => {
        clearError(key);
        clearError('remoteCallUrl');
        const remoteCallUrl =
            newEntry.typeOfEvent === APPOINTMENT_SLOT || !location?.remote
                ? ''
                : newEntry.remoteCallUrl;
        const additionalDetails =
            location?.name?.toLowerCase() !== 'community' ? '' : newEntry.additionalDetails;

        setNewEntry((prev) => ({
            ...prev,
            location,
            remoteCallUrl,
            additionalDetails,
            [key]: value
        }));
    };

    const onUpdateTypeOfEvent = (type) => {
        setErrors({});
        let updatedEntry = {
            ...initialState,
            date: newEntry.date,
            startTime: newEntry.startTime,
            endTime: newEntry.endTime,
            userId: newEntry.userId
        };
        let numberOfSlots = null;
        if (type === APPOINTMENT_SLOT) numberOfSlots = 1;
        if (type === USER_EVENT) dispatch(loadUserEventTypes());
        setShowUntilDate(false);
        setNewEntry({
            ...updatedEntry,
            typeOfEvent: type,
            numberOfSlots
        });
    };

    const onUpdateRepeat = (option) => {
        clearError('repeatUntilDate');
        setShowUntilDate(option !== 'NO_REPEAT');
        setNewEntry((prev) => ({ ...prev, repeat: option, repeatUntilDate: '' }));
    };

    const onUpdateParticipant = (participantId) => {
        const participant = participantId
            ? filteredParticipants.find((el) => el.id === participantId)
            : '';
        if (!participant) return;
        onUpdate('participantId', participantId);
        setNewEntry((prev) => ({ ...prev, participant: participant.name, mandatory: false }));
        setCurrentParticipant(participant);
        dispatch(setCurrentlySelectedParticipant(participant));
    };

    const onUpdateCapacity = (e) => {
        const value = e.target.value.slice(0, 2);
        onUpdate('numberOfSlots', value < 1 ? 1 : value);
    };

    const onUpdateUserEventType = (id) => {
        const userEventType = userEventTypes.find((el) => el.id === id)?.name || '';
        onUpdate('userEventTypeId', id);
        setNewEntry((prev) => ({ ...prev, userEventType }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsDisabledSaveButton(true);
        const {
            /* eslint-disable no-unused-vars */
            id,
            typeOfEvent,
            type,
            userEventTypeId,
            userEventType,
            location,
            service,
            participant,
            participantId,
            participantIds,
            numberOfSlots,
            mandatory,
            remoteCallUrl,
            additionalDetails,
            teamId,
            repeat,
            repeatUntilDate,
            notes,
            ...rest
        } = newEntry;

        if (typeOfEvent === APPOINTMENT_SLOT) {
            const newErrors = validateSlots(newEntry);

            if (Object.values(newErrors).some((el) => el.error)) {
                setErrors(newErrors);
                return;
            }
            const participantIds = selectedParticipants.map((el) => el.id);
            const payload = {
                ...rest,
                numberOfSlots,
                participantIds,
                teamId,
                additionalDetails
            };
            calendar === 'schedulingAssistant'
                ? dispatch(createSchedulingAssistantAppointmentSlot(newEntry.type, payload))
                : dispatch(createUserCalendarAppointmentSlot(newEntry.type, payload));
        }
        if (typeOfEvent === APPOINTMENT) {
            const newErrors = validateAppointment(newEntry);
            if (Object.values(newErrors).some((el) => el.error)) {
                setErrors(newErrors);
                return;
            }
            const payload = {
                ...rest,
                participantId,
                mandatory,
                additionalDetails,
                remoteCallUrl: remoteCallUrl ? encodeURI(remoteCallUrl) : null
            };
            calendar === 'schedulingAssistant'
                ? dispatch(createSchedulingAssistantAppointment(newEntry.type, payload))
                : dispatch(createUserCalendarAppointment(newEntry.type, payload));
        }

        if (typeOfEvent === USER_EVENT) {
            const newErrors = validateUserEvent(newEntry);
            if (Object.values(newErrors).some((el) => el.error)) {
                setErrors(newErrors);
                return;
            }
            const payload = {
                userId: loggedInUser.id,
                userEventTypeId,
                startTime: newEntry.startTime,
                endTime: newEntry.endTime,
                date: newEntry.date,
                repeat,
                repeatUntilDate,
                notes
            };
            dispatch(createUserEvent(userEventType, payload));
        }
    };

    // RENDER
    return (
        <Panel width="500px" open={openAppointmentSchedulerPanel} onToggle={onPanelClose}>
            <div className={sidePanel.createFormWrapper}>
                <form onSubmit={onSubmit} data-testid="user_appointment_create_form">
                    <RadioButtons
                        id="typeOfEvent"
                        disabled={isCreated}
                        radioButtons={typeOfEventRadioButtons}
                        customClass="listStyleReversed"
                        value={newEntry.typeOfEvent}
                        onChange={(option) => onUpdateTypeOfEvent(option)}
                    />
                    {newEntry.typeOfEvent !== USER_EVENT && (
                        <SingleSelect
                            id="serviceId"
                            label="Service"
                            placeholder="Select a service..."
                            disabled={!isEditable || isCreated}
                            mandatory={true}
                            menuItems={filteredServices || []}
                            selectedId={newEntry.serviceId || ''}
                            selected={serviceDetailsById[newEntry.serviceId]}
                            error={errors.serviceId}
                            onChange={(id) => onServiceChange(id)}
                        />
                    )}

                    {newEntry.typeOfEvent === USER_EVENT && (
                        <SingleSelect
                            id="userEventTypeId"
                            label="Event Type"
                            placeholder="Select event type..."
                            disabled={isCreated}
                            mandatory={true}
                            menuItems={filteredUserEventTypes || []}
                            selectedId={newEntry.userEventTypeId || ''}
                            selected={getObjFromId(
                                filteredUserEventTypes,
                                newEntry.userEventTypeId
                            )}
                            error={errors.userEventTypeId}
                            onChange={onUpdateUserEventType}
                        />
                    )}

                    {newEntry.typeOfEvent === APPOINTMENT_SLOT && (
                        <SingleSelect
                            id="teamId"
                            label="Team"
                            placeholder="Select team..."
                            disabled={!newEntry.serviceId || isCreated}
                            mandatory={true}
                            menuItems={filteredTeams || []}
                            selectedId={newEntry.teamId || ''}
                            selected={getObjFromId(filteredTeams, newEntry.teamId)}
                            error={errors.teamId}
                            onChange={(chosenId) => onUpdate('teamId', chosenId)}
                        />
                    )}

                    {newEntry.typeOfEvent !== USER_EVENT && (
                        <>
                            <StaticField
                                label="Adviser"
                                content={currentCalendarOwner.loginEmailAddress}
                            />
                            <AppointmentTypeSelect
                                appointmentTypes={filteredAppointmentTypes}
                                appointmentTypeId={newEntry.typeId}
                                disabled={!newEntry.serviceId || isCreated}
                                onAppointmentTypeChange={onUpdateAppointmentType}
                                errors={errors}
                            />
                            <LocationSelect
                                locationId={newEntry.locationId}
                                serviceId={newEntry.serviceId}
                                errors={errors}
                                disabled={!newEntry.serviceId || isCreated}
                                onLocationChange={onUpdateLocation}
                            />
                        </>
                    )}
                    {newEntry.typeOfEvent === APPOINTMENT && newEntry.location?.remote && (
                        <TextInputField
                            id={'remoteCallUrl'}
                            label={'Remote Call URL'}
                            placeholder={'Enter a remote call URL'}
                            maxLength={MAX_URL}
                            value={newEntry.remoteCallUrl || ''}
                            error={errors.remoteCallUrl}
                            onChange={(e) =>
                                onUpdate('remoteCallUrl', e.target.value.toLowerCase())
                            }
                        />
                    )}
                    {(newEntry.typeOfEvent === APPOINTMENT ||
                        newEntry.typeOfEvent === APPOINTMENT_SLOT) &&
                        newEntry?.location?.name?.toLowerCase() === 'community' && (
                            <TextInputField
                                id="additionalDetails"
                                label="Additional Details"
                                placeholder="Enter additional details..."
                                maxLength={ADDITIONAL_DETAILS_LIMIT}
                                value={newEntry.additionalDetails || ''}
                                onChange={(e) => onUpdate('additionalDetails', e.target.value)}
                            />
                        )}
                    <StartAndEndTimesSelect
                        startTime={newEntry.startTime}
                        endTime={newEntry.endTime}
                        disabled={isCreated}
                        errors={errors}
                        onTimeChange={onUpdateTime}
                    />
                    <DateSelect
                        mandatory={true}
                        value={newEntry.date}
                        disabled={isCreated}
                        error={errors.date}
                        onDateChange={(date) => onUpdateDate(date)}
                    />
                    {newEntry.typeOfEvent === USER_EVENT && (
                        <>
                            <RadioButtons
                                id="repeat"
                                label="Repeat"
                                disabled={isCreated}
                                radioButtons={[
                                    {
                                        id: 'noRepeat',
                                        label: 'No Repeat',
                                        onChangeVal: 'NO_REPEAT'
                                    },
                                    {
                                        id: 'daily',
                                        label: 'Daily',
                                        onChangeVal: 'DAILY'
                                    },
                                    { id: 'weekly', label: 'Weekly', onChangeVal: 'WEEKLY' },
                                    { id: 'monthly', label: 'Monthly', onChangeVal: 'MONTHLY' }
                                ]}
                                customClass="listStyle"
                                value={newEntry.repeat}
                                onChange={(option) => onUpdateRepeat(option)}
                            />

                            {showUntilDate && (
                                <div className={app.fadein}>
                                    <DateSelect
                                        label="Until Date"
                                        mandatory={true}
                                        value={newEntry.repeatUntilDate}
                                        disabled={isCreated}
                                        error={errors.repeatUntilDate}
                                        onDateChange={(date) => onUpdate('repeatUntilDate', date)}
                                    />
                                </div>
                            )}
                            <TextAreaField
                                id="notes"
                                label="Event Notes"
                                placeholder={'Enter event notes...'}
                                disabled={isCreated}
                                maxLength={_NOTES_LIMIT}
                                value={newEntry.notes}
                                count={`${newEntry.notes.length}/${_NOTES_LIMIT}`}
                                onChange={(e) => onUpdate('notes', e.target.value)}
                            />
                        </>
                    )}
                    {newEntry.typeOfEvent === APPOINTMENT && (
                        <>
                            <SingleSelect
                                id="participantId"
                                label="Participant"
                                placeholder="Select participant"
                                disabled={!newEntry.serviceId || isCreated}
                                mandatory={true}
                                menuItems={filteredParticipants || []}
                                selectedId={newEntry.participantId || ''}
                                selected={
                                    filteredParticipants.find(
                                        (el) => el.id === newEntry.participantId
                                    ) || {}
                                }
                                error={errors.participantId}
                                onChange={onUpdateParticipant}
                            />

                            <MandatoryActivitiesSwitch
                                isMandatory={newEntry.mandatory}
                                disabled={isCreated}
                                onSwitch={onUpdate}
                            />
                        </>
                    )}
                    {newEntry.typeOfEvent === APPOINTMENT_SLOT && (
                        <TextInputField
                            id="numberOfSlots"
                            type="number"
                            label="Number of slots"
                            min={1}
                            max={99}
                            disabled={isCreated}
                            value={newEntry.numberOfSlots || 1}
                            customClass="capacityInput"
                            onChange={onUpdateCapacity}
                        />
                    )}
                    <div className={sidePanel.updateButton}>
                        <Button
                            id="appointmentCreateButton"
                            content="Save"
                            disabled={isDisabledSaveButton}
                        />
                    </div>
                    {newCreatedAppointment?.id && currentParticipant && (
                        <StartAppointmentRow
                            onPanelClose={onPanelClose}
                            disabled={isDisabledStartAppointmentButton}
                            appointment={newCreatedAppointment}
                            participant={currentParticipant}
                        />
                    )}
                </form>
            </div>
        </Panel>
    );
};

UserAppointmentCreate.propTypes = {
    newAppointment: PropTypes.object,
    currentCalendarOwner: PropTypes.object,
    calendar: PropTypes.string
};

export default UserAppointmentCreate;
